import { useState, useEffect, useCallback, useRef } from 'react';
import { ViolinPlotResponse } from '../../../../../types/common';
import { getApiUrl } from '../../../../../config/environment';
import { getAuthToken } from '../../../../../core/auth/supabase-auth';
import { UseViolinPlotsReturn } from '../types';

interface UseViolinPlotsProps {
  sessionId: string;
}

/**
 * Custom hook for fetching and managing violin plot data
 */
export const useViolinPlots = ({ sessionId }: UseViolinPlotsProps): UseViolinPlotsReturn => {
  const [violinPlots, setViolinPlots] = useState<ViolinPlotResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());

  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchViolinPlots = useCallback(async () => {
    // Cancel previous request if exists
    if (abortControllerRef.current) {
      try {
        abortControllerRef.current.abort();
      } catch {
        // Ignore AbortError - this is expected when cancelling previous requests
      }
    }

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setIsLoading(true);
    setError(null);
    setImageErrors(new Set());

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(
        getApiUrl(`/api/training/visualizations/${sessionId}`),
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          signal
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ViolinPlotResponse = await response.json();

      if (!data.plots) {
        throw new Error(data.error || data.message || 'Failed to fetch visualization data');
      }

      // Type information now comes from backend in plot data
      setViolinPlots(data);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return; // Request was cancelled, not an error
      }
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
    } finally {
      if (!signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) {
      fetchViolinPlots();
    }

    return () => {
      // Abort the fetch request on cleanup
      // Suppress AbortError in React Strict Mode (development only)
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [sessionId, fetchViolinPlots]);

  const handleRefresh = useCallback(() => {
    fetchViolinPlots();
  }, [fetchViolinPlots]);

  const handleImageError = useCallback((plotName: string) => {
    setImageErrors(prev => new Set(prev).add(plotName));
  }, []);

  return {
    violinPlots,
    isLoading,
    error,
    imageErrors,
    handleRefresh,
    handleImageError
  };
};

export default useViolinPlots;
